/**
 * header-nav-responsive.js
 *
 * The hamburger-vs-navbar SWAP itself is done in pure CSS — a media query in
 * sections/header.liquid (`@media (max-width: 1399.98px)`) — so the correct
 * layout is present on first paint, identically on tablet load and on resize,
 * with no flash and no dependency on this script.
 *
 * This script only handles the one thing CSS cannot: reconcile the native
 * <details> `open` attribute at the 1400px breakpoint (see the
 * disclosure-ownership note below) — without this the desktop navbar would not
 * reappear after collapsing, since the theme keeps the disclosure open at
 * >=769px. The search bar's full-width-row layout is pure CSS (in
 * sections/header.liquid), so nothing is measured on resize.
 *
 * It also mirrors the collapsed state into `.header--nav-collapsed`, which the
 * theme's inline NavMenu reads — but that class no longer drives any styling.
 *
 * The theme core switches its own mobile/desktop nav at 769px
 * (theme.mediaMatches.md); we use 1400px so the desktop nav never overflows on
 * tablets / small laptops, matching the CSS breakpoint above.
 *
 * Disclosure (open/closed) ownership — IMPORTANT:
 *   The nav lives inside a native <details class="main-menu__disclosure">. A
 *   native <details> hides ALL of its content while `open` is false. So the
 *   correct `open` state differs per layout and we must assert it ourselves,
 *   because the theme keeps the disclosure open at >=769px and would otherwise
 *   leave the collapsed hamburger menu open, or fight us on resize:
 *     - Collapsed (hamburger):  open = false  → menu starts closed, the
 *                               hamburger toggle opens it on click.
 *     - Desktop nav:            open = true   → the inline navbar renders.
 *
 * Does not modify any theme core files.
 */
(function () {
  'use strict';

  var DESKTOP_NAV = window.matchMedia('(min-width: 1400px)');
  var THEME_MD    = window.matchMedia('(min-width: 769px)');
  var header      = document.querySelector('store-header');

  if (!header) return;

  function isCollapsed() {
    return header.classList.contains('header--nav-collapsed');
  }

  /**
   * Close the main hamburger menu and any auto-opened mega-menus. Used when
   * entering the collapsed state so the menu behaves as a closed hamburger.
   */
  function closeDisclosures() {
    document.querySelectorAll('.main-menu__disclosure').forEach(function (d) {
      d.classList.remove('is-open');
      d.open = false;
    });

    // Mega-menus are auto-opened by the theme at >=769px. Close them whenever
    // the hamburger is active so they behave as collapsible items.
    document.querySelectorAll('.js-mega-nav > details[open]').forEach(function (d) {
      d.classList.remove('is-open');
      d.open = false;
    });
  }

  /**
   * Re-open the main disclosure so the inline desktop navbar renders, and clear
   * any body scroll-lock the hamburger menu may have left behind. Used when
   * expanding from the collapsed state back to the full desktop nav.
   */
  function restoreDesktopNav() {
    var disclosure = header.querySelector('.main-menu__disclosure');
    if (disclosure && !disclosure.open) {
      disclosure.classList.remove('is-closing');
      disclosure.open = true;
    }

    // If the hamburger menu was open on a tablet, the theme locked body scroll.
    // Clear it on the way to desktop, but only if a header disclosure set it —
    // never interfere with the cart drawer or other overlays.
    var menuWasOpen = header.querySelector('.main-menu__disclosure.is-open');
    if (menuWasOpen || document.body.classList.contains('fixed')) {
      document.body.classList.remove('overflow-hidden', 'fixed');
    }
  }

  /**
   * Toggle the collapsed class based on the 1400px breakpoint. The collapse /
   * expand side effects only run on an actual transition, so resizing *within*
   * a layout (e.g. 800px -> 900px) never closes a menu the user has opened.
   */
  function setCollapsedClass() {
    var wasCollapsed = isCollapsed();
    var collapsed = !DESKTOP_NAV.matches;

    header.classList.toggle('header--nav-collapsed', collapsed);

    if (collapsed && !wasCollapsed) {
      closeDisclosures();   // desktop -> hamburger
    } else if (!collapsed && wasCollapsed) {
      restoreDesktopNav();  // hamburger -> desktop
    }
  }

  /**
   * Force the disclosure into the correct open/closed state for the current
   * layout. Called only on breakpoint changes (and load), with retries, to win
   * any race with the theme's own breakpoint handler — never on every resize
   * tick, so it can't slam shut a menu the user opened while resizing.
   */
  function assertDisclosureForLayout() {
    if (isCollapsed()) {
      closeDisclosures();
    } else {
      var disclosure = header.querySelector('.main-menu__disclosure');
      if (disclosure && !disclosure.open) {
        disclosure.classList.remove('is-closing');
        disclosure.open = true;
      }
    }
  }

  /**
   * Run when a breakpoint is crossed (and on load): update the collapsed class,
   * then assert the disclosure state with retries to win any race with the
   * theme's own breakpoint handler.
   *
   * The search bar's wrap-to-its-own-row is handled entirely in CSS
   * (the search sits on its own full-width row, all in CSS) — there is
   * deliberately no width measurement here, so the icons never flash on resize.
   */
  function handleBreakpointChange() {
    setCollapsedClass();
    assertDisclosureForLayout();
    // The theme re-opens the nav on this SAME event via a setTimeout(0) (it
    // treats >=769px as desktop). A rAF runs after that timeout but BEFORE the
    // next paint, so closing here removes the menu before it can flash on screen
    // while resizing. The later timeouts are a backup for any async theme work.
    requestAnimationFrame(assertDisclosureForLayout);
    setTimeout(assertDisclosureForLayout, 50);
    setTimeout(assertDisclosureForLayout, 150);
  }

  // Viewport crossed our 1400px breakpoint, or the theme's 769px one.
  DESKTOP_NAV.addEventListener('change', handleBreakpointChange);
  THEME_MD.addEventListener('change', handleBreakpointChange);

  // The theme's MainMenu re-evaluates its disclosure state on this event; run
  // after it so our layout's desired state wins.
  window.addEventListener('on:breakpoint-change', handleBreakpointChange);

  // Page load: run after deferred theme JS has initialised, then re-assert a
  // couple of times to catch asynchronous class additions.
  document.addEventListener('DOMContentLoaded', function () {
    requestAnimationFrame(function () {
      requestAnimationFrame(handleBreakpointChange);
    });
  });
}());
